import { useCallback, useMemo } from 'react';
import { matchPath, useNavigate } from 'react-router-dom';
import { COMMAND_CENTER_BASE, flowHome } from '../../../../../../../mini-apps/_shared/flow-links';
import type { MiniAppShellRailConfig } from '../../../../../../../mini-apps/_shared/types';
import { useUserControlsContext, useUserControlsLoaded } from '../../../../contexts/UserControlsContext';
import type { FlowIconName } from '../../icons';
import { useRoleView } from '../../../../hooks/useRoleView';
import { useLastSubtab } from '../flow-last-subtab-store';
import { setFlowClosed } from '../panels/flow-panels-store';
import { visibleSubtabForTeam } from '../role-subtabs';

/**
 * The v2 rail's item spec: one pure table the NavRail renders from, plus the
 * gates carried over from the old `components/NavigationRail.tsx`. Footer rows
 * (Apps, Chat, avatar) are not here; they are fixed in `NavRail.tsx` and
 * driven by `RailOverrides`.
 */

// User type surface access: controls which product views are visible.
// Admin access is handled separately via the UserControlsContext.
export type AppViewMode = 'categories' | 'accounts' | 'pipeline';

const USER_TYPE_SURFACES: Record<string, readonly AppViewMode[]> = {
  MEMBER: ['accounts', 'pipeline', 'categories'] as const,
  PRE_SALES: ['accounts', 'pipeline', 'categories'] as const,
  POST_SALES: ['accounts', 'categories'] as const,
  REVOPS: ['accounts', 'pipeline', 'categories'] as const,
};

export function canAccessSurface(userType: string | null | undefined, surface: AppViewMode): boolean {
  const type = userType ?? 'MEMBER';
  const surfaces = USER_TYPE_SURFACES[type];
  if (!surfaces) return USER_TYPE_SURFACES.MEMBER.includes(surface);
  return surfaces.includes(surface);
}

/** `undefined` gate = every user; a surface = `canAccessSurface`; `manager` = `isManager`. */
type RailGate = AppViewMode | 'manager';

export interface RailItem {
  key: string;
  label: string;
  to: string;
  /** Path patterns that light the item; `/` matches only the exact root. */
  active: readonly string[];
  icon: FlowIconName;
  gate?: RailGate;
}

export const RAIL_ITEMS: readonly RailItem[] = [
  { key: 'home', label: 'Home', to: '/home', active: ['/home', '/'], icon: 'home' },
  { key: 'flow', label: 'Flow', to: COMMAND_CENTER_BASE, active: ['/flow'], icon: 'flow' },
  { key: 'toolkit', label: 'Toolkit', to: '/library', active: ['/library', '/skills', '/personas'], icon: 'toolkit' },
  { key: 'accounts', label: 'Accounts', to: '/accounts', active: ['/accounts'], icon: 'accounts', gate: 'accounts' },
  { key: 'pipeline', label: 'Pipeline', to: '/pipeline', active: ['/pipeline'], icon: 'pipeline', gate: 'pipeline' },
  { key: 'categories', label: 'Categories', to: '/categories', active: ['/categories'], icon: 'categories', gate: 'categories' },
  { key: 'open-loops', label: 'Open Loops', to: '/open-loops', active: ['/open-loops'], icon: 'openLoops' },
  { key: 'signals', label: 'Signals', to: '/signals', active: ['/signals'], icon: 'signals' },
  { key: 'my-actions', label: 'My Actions', to: '/my-actions', active: ['/my-actions'], icon: 'myActions' },
  { key: 'my-team', label: 'My Team', to: '/my-team/health', active: ['/my-team'], icon: 'myTeam', gate: 'manager' },
];

export type RailOverrides = Pick<MiniAppShellRailConfig, 'hideNavKeys' | 'hideTools' | 'hideUserMenu' | 'hideLogo'>;

/**
 * Every rail navigation (strip, logo, mobile menu) goes through here so it
 * always lands on the page: ShellLayout resets Discover on pathname change
 * only, so each click clears it explicitly. The Flow link falls back to the
 * default tab when the remembered one is hidden for the rep's role.
 */
export function useRailNavigate() {
  const navigate = useNavigate();
  const lastSubtab = useLastSubtab();
  const { team, isLoaded } = useRoleView();
  const go = useCallback(
    (to: string) => {
      setFlowClosed(false);
      navigate(to);
    },
    [navigate],
  );
  const goFlow = useCallback(() => go(flowHome(visibleSubtabForTeam(lastSubtab, team, isLoaded))), [go, lastSubtab, team, isLoaded]);
  const goItem = useCallback((item: RailItem) => (item.key === 'flow' ? goFlow() : go(item.to)), [go, goFlow]);
  return { go, goFlow, goItem };
}

/**
 * The key of the item whose patterns match `pathname`, or null (admin,
 * `/tools/*`, `/artifact/*` and unknown routes light nothing).
 */
export function isActiveKey(pathname: string, items: readonly RailItem[]): string | null {
  for (const item of items) {
    if (item.active.some((path) => matchPath({ path, end: path === '/' }, pathname) !== null)) return item.key;
  }
  return null;
}

/**
 * Role-gated items render only once the controls have loaded, so the rail does
 * not flash the wrong set. `hiddenNavKeys` is the manifest's
 * `shell.rail.hideNavKeys` only.
 */
export function useRailItems(overrides: RailOverrides): readonly RailItem[] {
  const { isManager, userType } = useUserControlsContext();
  const loaded = useUserControlsLoaded();
  const hideNavKeys = overrides.hideNavKeys;
  return useMemo(() => {
    const hidden = new Set(hideNavKeys ?? []);
    return RAIL_ITEMS.filter((item) => {
      if (hidden.has(item.key)) return false;
      if (!item.gate) return true;
      if (!loaded) return false;
      return item.gate === 'manager' ? isManager : canAccessSurface(userType, item.gate);
    });
  }, [hideNavKeys, loaded, isManager, userType]);
}
